import { Container, Rectangle, Sprite, Texture } from 'pixi.js';
import { prefersReducedMotion } from '../board/ui';
import { glowTexture } from '../road/glow';
import type { SceneLayoutContext } from '../../scenes/SceneComponent';
import type { AmbientLifeConfig } from '../../theme/Theme';

/** The painted creatures, looked up again until they have loaded. */
export interface AmbientLifeArt {
  butterflies: () => Texture[];
  /** A bird with its wings up, then down. */
  bird: () => [Texture, Texture] | null;
  /** What covers the sky above the reels right now (the road panel), in
   *  screen pixels, or null: read when birds set off. */
  cover?: () => { left: number; right: number; bottom: number } | null;
}

type Zone = { x: number; y: number; w: number; h: number };

/** Butterflies by the size of the screen, in CSS pixels across. */
const BUTTERFLY_SIZE = { phone: 30, wide: 42 };
const BIRD_SIZE = { phone: 26, wide: 34 };

/**
 * The slot's background, alive: a few butterflies flitting about the
 * open ground, now and then a bird or two crossing the sky, and dappled
 * light drifting over the scene. It sits under the reels and everything
 * else, and the butterflies keep to the ground beside and below them, so
 * nothing ever crosses the reels. Cheap: a handful of sprites moved by
 * plain maths each frame, still while the page is hidden, and nothing at
 * all for players who ask for reduced motion.
 */
export class AmbientLife extends Container {
  private readonly lightLayer = new Container();
  private readonly creatureLayer = new Container();
  private readonly butterflies: Butterfly[] = [];
  private readonly birds: Bird[] = [];
  private readonly patches: Patch[] = [];
  private readonly beams: Beam[] = [];
  private zones: Zone[] = [];
  private ctx: SceneLayoutContext | null = null;
  private below = 0;
  private viewW = 1;
  private viewH = 1;
  private clock = 0;
  private nextBird = 0;
  private wide = false;
  /** Off for reduced motion: nothing is made or moved. */
  readonly enabled: boolean;

  constructor(
    private readonly config: AmbientLifeConfig,
    private readonly art: AmbientLifeArt,
  ) {
    super();
    this.enabled = !prefersReducedMotion();
    this.eventMode = 'none';
    this.addChild(this.lightLayer, this.creatureLayer);
    this.visible = this.enabled;
    if (!this.enabled) return;
    const light = config.light;
    if (light) {
      for (let i = 0; i < (light.patches ?? 5); i++) this.patches.push(new Patch(this.lightLayer, light.tint, i));
      for (let i = 0; i < (light.beams ?? 0); i++) this.beams.push(new Beam(this.lightLayer, light.tint, i));
    }
    this.nextBird = config.birdsEverySec ? 2 + Math.random() * config.birdsEverySec * 0.5 : Infinity;
  }

  /** What's moving, for the browser tests and screenshots. */
  get snapshot(): { butterflies: number; birds: number; patches: number; beams: number } {
    return { butterflies: this.butterflies.length, birds: this.birds.length, patches: this.patches.length, beams: this.beams.length };
  }

  /**
   * Find the open ground around the reels: beside them, below the
   * buttons and above them under the top bar. The butterflies keep to
   * these; birds cross the highest.
   */
  layout(ctx: SceneLayoutContext): void {
    this.ctx = ctx;
    this.viewW = ctx.width;
    this.viewH = ctx.height;
    this.wide = ctx.width >= 700;
    const left = ctx.machineLeft ?? 0;
    const right = ctx.machineRight ?? ctx.width;
    const top = 64;
    const below = ctx.spinButtonY + 54;
    const zones: Zone[] = [];
    const add = (x: number, y: number, w: number, h: number) => {
      if (w >= 70 && h >= 70) zones.push({ x, y, w, h });
    };
    add(8, top, left - 22, ctx.height - top - 16);
    add(right + 14, top, ctx.width - right - 22, ctx.height - top - 16);
    add(8, below, ctx.width - 16, ctx.height - below - 12);
    this.zones = zones;
    this.below = below;
    for (const p of this.patches) p.place(ctx.width, ctx.height);
    for (const b of this.beams) b.place(ctx.width, ctx.height);
    for (const b of this.butterflies) b.rehome(this.pickZone());
  }

  /** Move everything on by `deltaMs`. */
  tick(deltaMs: number): void {
    if (!this.enabled || !this.visible) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    const dt = Math.min(0.05, deltaMs / 1000);
    this.clock += dt;
    for (const p of this.patches) p.update(this.clock);
    for (const b of this.beams) b.update(this.clock);
    this.updateButterflies(dt);
    this.updateBirds(dt);
  }

  private updateButterflies(dt: number): void {
    const want = this.zones.length > 0 ? this.config.butterflies ?? 0 : 0;
    const textures = this.art.butterflies();
    if (this.butterflies.length < want && textures.length > 0) {
      const texture = textures[this.butterflies.length % textures.length]!;
      const size = this.wide ? BUTTERFLY_SIZE.wide : BUTTERFLY_SIZE.phone;
      const b = new Butterfly(texture, size * (0.85 + Math.random() * 0.3), this.pickZone());
      this.butterflies.push(b);
      this.creatureLayer.addChild(b);
    }
    for (const b of this.butterflies) b.update(dt, () => this.pickZone());
  }

  private updateBirds(dt: number): void {
    const every = this.config.birdsEverySec;
    const frames = this.art.bird();
    const sky = every && frames && this.clock >= this.nextBird && this.birds.length === 0 ? this.skyBand() : null;
    if (every && frames && sky) {
      this.nextBird = this.clock + every * (0.6 + Math.random() * 0.8);
      const flock = 1 + Math.floor(Math.random() * 3);
      const fromLeft = Math.random() < 0.5;
      const y = sky.y + sky.h * (0.2 + Math.random() * 0.6);
      const size = this.wide ? BIRD_SIZE.wide : BIRD_SIZE.phone;
      for (let i = 0; i < flock; i++) {
        const bird = new Bird(frames, size * (0.8 + Math.random() * 0.35), fromLeft, this.viewW, y + (i % 2 ? 1 : -1) * i * size * 0.7, i * size * 1.6);
        this.birds.push(bird);
        this.creatureLayer.addChild(bird);
      }
    }
    for (let i = this.birds.length - 1; i >= 0; i--) {
      const bird = this.birds[i]!;
      if (!bird.update(dt, this.viewW)) {
        bird.destroy();
        this.birds.splice(i, 1);
      }
    }
  }

  /**
   * Where birds cross: the sky above the reels. A panel as wide as the
   * screen there (a phone's road panel) would hide them, so they fly in
   * the gap under it; with no sky at all, over the ground below.
   */
  private skyBand(): Zone | null {
    const ctx = this.ctx;
    if (!ctx) return null;
    const cover = this.art.cover?.() ?? null;
    const wide = cover !== null && cover.right - cover.left >= ctx.width * 0.8;
    const top = wide ? cover.bottom + 4 : 54;
    const h = ctx.machineTop - 8 - top;
    if (h >= 26) return { x: 0, y: top, w: ctx.width, h };
    return this.zones.find((z) => z.y >= this.below) ?? null;
  }

  private pickZone(): Zone {
    const zones = this.zones;
    if (zones.length === 0) return { x: 0, y: 0, w: this.viewW, h: this.viewH };
    const total = zones.reduce((sum, z) => sum + z.w * z.h, 0);
    let pick = Math.random() * total;
    for (const z of zones) {
      pick -= z.w * z.h;
      if (pick <= 0) return z;
    }
    return zones[zones.length - 1]!;
  }
}

/**
 * One butterfly, seen from above: its two wings are the two halves of the
 * painting, folding in towards the body as it flaps. It wanders from
 * point to point in its patch of ground with a flutter, now and then
 * gliding with its wings held open.
 */
class Butterfly extends Container {
  private readonly leftWing: Sprite;
  private readonly rightWing: Sprite;
  private vx = 0;
  private vy = 0;
  private tx = 0;
  private ty = 0;
  private phase = Math.random() * Math.PI * 2;
  private readonly flapHz = 9 + Math.random() * 4;
  private glide = 0;
  private retarget = 0;
  private heading = 0;
  private wobble = Math.random() * 10;
  private zone: Zone;

  constructor(texture: Texture, size: number, zone: Zone) {
    super();
    const half = texture.frame.width / 2;
    const left = new Texture({ source: texture.source, frame: new Rectangle(texture.frame.x, texture.frame.y, half, texture.frame.height) });
    const right = new Texture({ source: texture.source, frame: new Rectangle(texture.frame.x + half, texture.frame.y, half, texture.frame.height) });
    this.leftWing = new Sprite(left);
    this.rightWing = new Sprite(right);
    this.leftWing.anchor.set(1, 0.5);
    this.rightWing.anchor.set(0, 0.5);
    this.addChild(this.leftWing, this.rightWing);
    const k = size / texture.frame.width;
    this.scale.set(k);
    this.zone = zone;
    this.position.set(zone.x + Math.random() * zone.w, zone.y + Math.random() * zone.h);
    this.pickTarget();
  }

  rehome(zone: Zone): void {
    this.zone = zone;
    if (!inside(this.x, this.y, zone)) this.position.set(zone.x + Math.random() * zone.w, zone.y + Math.random() * zone.h);
    this.pickTarget();
  }

  update(dt: number, nextZone: () => Zone): void {
    this.retarget -= dt;
    const dx = this.tx - this.x;
    const dy = this.ty - this.y;
    const dist = Math.hypot(dx, dy);
    if (dist < 16 || this.retarget <= 0) {
      // Now and then it moves on to another patch of ground.
      if (Math.random() < 0.15) this.zone = nextZone();
      this.pickTarget();
    }
    const speed = this.glide > 0 ? 34 : 52;
    const ax = (dx / Math.max(1, dist)) * speed - this.vx;
    const ay = (dy / Math.max(1, dist)) * speed - this.vy;
    this.vx += ax * Math.min(1, dt * 1.6);
    this.vy += ay * Math.min(1, dt * 1.6);
    // A flutter across its path, and a little bob.
    this.wobble += dt;
    const flutter = Math.sin(this.wobble * 5.3) * 18 + Math.sin(this.wobble * 8.1) * 8;
    const len = Math.max(1, Math.hypot(this.vx, this.vy));
    this.x += (this.vx + (-this.vy / len) * flutter) * dt;
    this.y += (this.vy + (this.vx / len) * flutter) * dt + Math.sin(this.wobble * 11) * 6 * dt;
    // Face where it's going (the painting faces up).
    const target = Math.atan2(this.vy, this.vx) + Math.PI / 2;
    let turn = target - this.heading;
    while (turn > Math.PI) turn -= Math.PI * 2;
    while (turn < -Math.PI) turn += Math.PI * 2;
    this.heading += turn * Math.min(1, dt * 4);
    this.rotation = this.heading;
    // Flap, or glide with the wings open.
    if (this.glide > 0) {
      this.glide -= dt;
      this.setWings(0.95);
    } else {
      this.phase += dt * this.flapHz * Math.PI * 2;
      this.setWings(0.2 + 0.8 * (0.5 + 0.5 * Math.cos(this.phase)));
      if (Math.random() < dt * 0.25) this.glide = 0.25 + Math.random() * 0.4;
    }
  }

  private setWings(open: number): void {
    this.leftWing.scale.x = open;
    this.rightWing.scale.x = open;
  }

  private pickTarget(): void {
    const z = this.zone;
    this.tx = z.x + Math.random() * z.w;
    this.ty = z.y + Math.random() * z.h;
    this.retarget = 2 + Math.random() * 3;
  }
}

/** A bird crossing the sky, wings up and down, now and then gliding. */
class Bird extends Sprite {
  private t = 0;
  private flap = Math.random();
  private glide = 0;
  private readonly speed = 110 + Math.random() * 50;
  private readonly dir: 1 | -1;
  private readonly baseY: number;

  constructor(
    private readonly frames: [Texture, Texture],
    size: number,
    fromLeft: boolean,
    viewW: number,
    y: number,
    lag: number,
  ) {
    super(frames[0]);
    this.anchor.set(0.5);
    this.dir = fromLeft ? 1 : -1;
    const k = size / frames[0].width;
    // The painting flies right; one going left is mirrored.
    this.scale.set(k * this.dir, k);
    this.baseY = y;
    this.position.set(fromLeft ? -size - lag : viewW + size + lag, y);
  }

  /** False once it has flown off the far side. */
  update(dt: number, viewW: number): boolean {
    this.t += dt;
    this.x += this.dir * this.speed * dt;
    this.y = this.baseY + Math.sin(this.t * 2.2) * 6;
    if (this.glide > 0) {
      this.glide -= dt;
      this.texture = this.frames[1];
    } else {
      this.flap += dt * 7;
      this.texture = this.frames[Math.floor(this.flap) % 2]!;
      if (Math.random() < dt * 0.3) this.glide = 0.4 + Math.random() * 0.5;
    }
    const w = Math.abs(this.width);
    return this.dir === 1 ? this.x < viewW + w * 2 : this.x > -w * 2;
  }
}

/** A soft patch of light that drifts slowly and breathes. */
class Patch {
  private readonly sprite = new Sprite(glowTexture());
  private cx = 0;
  private cy = 0;
  private rx = 0;
  private ry = 0;
  private readonly speed = 0.22 + Math.random() * 0.16;
  private readonly offset = Math.random() * 100;
  private readonly strength: number;

  constructor(layer: Container, tint: number, private readonly index: number) {
    this.sprite.anchor.set(0.5);
    this.sprite.blendMode = 'add';
    this.sprite.tint = tint;
    this.strength = 0.14 + Math.random() * 0.1;
    layer.addChild(this.sprite);
  }

  place(w: number, h: number): void {
    // Over the lower part of the scene, where light falls on the ground.
    this.cx = w * (0.1 + Math.random() * 0.8);
    this.cy = h * (0.45 + Math.random() * 0.45);
    this.rx = w * 0.06;
    this.ry = h * 0.04;
    const size = Math.max(w, h) * (0.16 + 0.1 * ((this.index * 37) % 10) / 10);
    this.sprite.width = size;
    this.sprite.height = size * 0.6;
  }

  update(t: number): void {
    const p = t * this.speed + this.offset;
    this.sprite.x = this.cx + Math.sin(p * 1.3) * this.rx;
    this.sprite.y = this.cy + Math.cos(p) * this.ry;
    // Sun through leaves: it drifts, and flickers a little as they move.
    this.sprite.alpha = this.strength * (0.5 + 0.35 * Math.sin(p * 2.1) + 0.15 * Math.sin(p * 7.3));
  }
}

let beamTexture: Texture | null = null;

/** A soft vertical shaft of light, bright at the top, fading down and at its sides. */
function beam(): Texture {
  if (beamTexture) return beamTexture;
  const w = 64;
  const h = 256;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const across = ctx.createLinearGradient(0, 0, w, 0);
    across.addColorStop(0, 'rgba(255,255,255,0)');
    across.addColorStop(0.5, 'rgba(255,255,255,1)');
    across.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = across;
    ctx.fillRect(0, 0, w, h);
    ctx.globalCompositeOperation = 'destination-in';
    const down = ctx.createLinearGradient(0, 0, 0, h);
    down.addColorStop(0, 'rgba(0,0,0,1)');
    down.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = down;
    ctx.fillRect(0, 0, w, h);
  }
  beamTexture = Texture.from(canvas);
  return beamTexture;
}

/** A slanting beam of light that sways a little and comes and goes. */
class Beam {
  private readonly sprite = new Sprite(beam());
  private x0 = 0;
  private readonly period = 7 + Math.random() * 5;
  private readonly offset = Math.random() * 20;

  constructor(layer: Container, tint: number, private readonly index: number) {
    this.sprite.anchor.set(0.5, 0);
    this.sprite.blendMode = 'add';
    this.sprite.tint = tint;
    this.sprite.rotation = -0.38;
    layer.addChild(this.sprite);
  }

  place(w: number, h: number): void {
    this.x0 = w * (0.12 + 0.3 * this.index);
    this.sprite.y = -h * 0.05;
    this.sprite.width = Math.max(90, w * 0.09);
    this.sprite.height = h * 1.1;
  }

  update(t: number): void {
    const p = ((t + this.offset) / this.period) * Math.PI * 2;
    this.sprite.x = this.x0 + Math.sin(p * 0.5) * 18;
    this.sprite.alpha = 0.05 + 0.09 * (0.5 + 0.5 * Math.sin(p));
  }
}

function inside(x: number, y: number, z: Zone): boolean {
  return x >= z.x && x <= z.x + z.w && y >= z.y && y <= z.y + z.h;
}
